import { Analytics } from '@cobliteam/analytics';
import type { Preferences } from '@cobliteam/common-types/src/preferences';
import type { RuntimeFlags } from '@cobliteam/dashboard/src/dashboardDef';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useAnnouncement } from '@cobliteam/hooks/src/useEntity/usePrismic';
import useFeatureFlags, { type FeatureFlags } from '@cobliteam/hooks/src/useFeatureFlags';
import useLocalStorage from '@cobliteam/hooks/src/useLocalStorage';
import { type Permission, usePermissions } from '@cobliteam/permissions';
import type { Content, RichTextField } from '@cobliteam/prismic';
import { isFilled, PrismicLink, PrismicRichText } from '@cobliteam/prismic';
import { Button } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { camelCase } from 'change-case';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DialogTypeformSurvey from '../../dialogs/DialogTypeformSurvey/DialogTypeformSurvey';
import { InfoCard, InfoCardContent, InfoCardDescription, InfoCardDismiss, InfoCardFooter, InfoCardMedia, InfoCardTitle } from '../../InfoCard';

const OFFSET_FACTOR = 5.5;
const SCALE_FACTOR = 0.03;
const OPACITY_FACTOR = 0.1;
const STORAGE_PREFIX = 'cobli-announcement-';
type AnnouncementCard = Content.AnnouncementCardDocument;

const getFirstRichTextText = (field: RichTextField | null | undefined): string => {
  if (!field?.length) return '';

  const firstNode = field[0];
  if (!firstNode) return '';
  return 'text' in firstNode && typeof firstNode.text === 'string' ? firstNode.text : '';
};

// Helper function to extract dismissed announcement IDs from preferences
const getDismissedAnnouncementIds = (preferences: Preferences | null): string[] => {
  if (!preferences) return [];

  return Object.keys(preferences)
    .filter((key) => key.startsWith(STORAGE_PREFIX))
    .map((key) => key.replace(STORAGE_PREFIX, ''));
};

// Helper function to check if announcement meets feature flag requirements
const meetsFeatureFlagRequirements = (announcement: any, featureFlags: any): boolean => {
  if (!featureFlags) return false;

  const requiredFeatureFlags = announcement.data?.feature_flags || [];
  if (requiredFeatureFlags.length === 0) return true;

  return requiredFeatureFlags.every(({ feature_flag }: { feature_flag: string }) => {
    if (!feature_flag) return false;

    const flagKey = camelCase(feature_flag) as keyof FeatureFlags;
    return featureFlags[flagKey] === true;
  });
};

// Helper function to check if announcement meets runtime flag requirements
const meetsRuntimeFlagRequirements = (announcement: any, runtimeFlags: any): boolean => {
  if (!runtimeFlags) return false;

  const requiredRuntimeFlags = announcement.data?.runtime_flags || [];
  if (requiredRuntimeFlags.length === 0) return true;

  return requiredRuntimeFlags.every(({ runtime_flag }: { runtime_flag: string }) => {
    if (!runtime_flag) return false;
    const runtimeFlagValue = runtimeFlags[runtime_flag as keyof RuntimeFlags];
    return typeof runtimeFlagValue === 'boolean' && runtimeFlagValue;
  });
};

// Helper function to check if announcement meets page route requirements
const meetsPageRouteRequirements = (announcement: any, currentPathname: string): boolean => {
  const requiredPageRoutes = announcement.data?.pages || [];
  if (requiredPageRoutes.length === 0) return true; // No page restriction

  return requiredPageRoutes.some(({ page_route }) => {
    if (!page_route) return false;
    return currentPathname.startsWith(page_route);
  });
};

// Helper function to check if announcement meets permission requirements
const meetsPermissionRequirements = (announcement: any, hasAllPermissions: (permissions: Permission[]) => boolean): boolean => {
  const requiredPermissions = announcement.data?.permissions || [];
  if (requiredPermissions.length === 0) return true; // No permission restriction

  const permissionsKeys = requiredPermissions.map(({ permission }) => permission).filter((permission): permission is Permission => Boolean(permission));

  return hasAllPermissions(permissionsKeys);
};

// Helper function to sort announcements by priority
const sortAnnouncementsByPriority = (announcements: AnnouncementCard[]): AnnouncementCard[] => {
  return announcements.sort((a, b) => {
    // Sort by pages first (fewer pages = higher priority)
    const aPages = a.data.pages || [];
    const bPages = b.data.pages || [];
    return aPages.length - bPages.length || a.id.localeCompare(b.id);
  });
};

const isTypeformLink = (link: string): boolean => {
  return link.includes('typeform.com');
};
/**
 * Extrai o ID do Typeform de um link, se presente.
 * @param link - O link do Typeform.
 * @returns O ID do Typeform como string, ou null se não encontrado.
 * @example
 *   getTypeformIdFromLink('https://form.typeform.com/to/oBmT1sU3'); // retorna 'oBmT1sU3'
 */
const getTypeformIdFromLink = (link: string): string | null => {
  if (!isTypeformLink(link)) return null;

  // Find typeform id in the URL pattern
  // Examples:
  // https://form.typeform.com/to/abcdef
  // https://cobli.typeform.com/to/abcdef
  // https://xyz.typeform.com/to/abcdef#someparam=something
  const match = link.match(/typeform\.com\/to\/([\w\d]+)/i);
  return match?.[1] || null;
};

// Helper function to calculate CSS variables for card positioning
const calculateCardPositioning = (index: number, totalCards: number) => {
  const cardsFromTop = totalCards - (index + 1);

  return {
    ['--scale' as string]: 1 - cardsFromTop * SCALE_FACTOR,
    ['--y' as string]: `-${cardsFromTop * OFFSET_FACTOR}%`,
    ['--opacity' as string]: 1 - cardsFromTop * OPACITY_FACTOR,
  };
};

// Helper function to create storage key for dismissed announcements
const createDismissedStorageKey = (announcementId: string): string => {
  return `${STORAGE_PREFIX}${announcementId}`;
};

const AppAnnouncementCard = () => {
  const featureFlags = useFeatureFlags();

  const { hasAllPermissions } = usePermissions();
  const { runtimeFlags } = useDashboard();
  const [openTypeformSurvey, setOpenTypeformSurvey] = useState<string | null>(null);

  const location = useLocation();
  const [preferences, setPreferences] = useLocalStorage<Preferences>('preferences');
  const { data: announcements, isLoading, isPending, error } = useAnnouncement({ options: { enabled: featureFlags?.announcementCard } });

  const dismissedIds = useMemo(() => {
    return getDismissedAnnouncementIds(preferences);
  }, [preferences]);

  const cards = useMemo(() => {
    if (!announcements) return [];

    const filteredCards = announcements
      .filter((item) => !dismissedIds.includes(item.id)) // Remove dismissed announcements
      .filter((item) => meetsFeatureFlagRequirements(item, featureFlags))
      .filter((item) => meetsRuntimeFlagRequirements(item, runtimeFlags))
      .filter((item) => meetsPageRouteRequirements(item, location.pathname))
      .filter((item) => meetsPermissionRequirements(item, hasAllPermissions));

    return sortAnnouncementsByPriority(filteredCards);
  }, [dismissedIds, featureFlags, announcements, runtimeFlags, location, hasAllPermissions]);

  const hasTrackedDisplay = useRef(false);

  useEffect(() => {
    if (hasTrackedDisplay.current || cards.length === 0) return;

    hasTrackedDisplay.current = true;
    Analytics.track('AppAnnouncement - Display', {
      cardCount: cards.length,
      cardIds: cards.map((card) => card.id),
      cardTitles: cards.map((card) => getFirstRichTextText(card.data.title)),
    });
  }, [cards]);

  const onDismiss = (id: string, title?: string) => {
    Analytics.track('AppAnnouncement - Dismiss', {
      announcementId: id,
      title: title || '',
    });

    const storageKey = createDismissedStorageKey(id);
    setPreferences({
      ...preferences,
      [storageKey]: true,
    });
  };

  if (!featureFlags?.announcementCard) return null;
  if (isLoading || isPending) return null;
  if (error) return null;
  return (
    <div className='fixed right-16 bottom-6 z-announcement w-[362px] rounded-xl'>
      {cards.map(({ data: { title, description, imagem, link, link_text }, id }, index) => {
        const isOnDisplay = index === cards.length - 1;
        const cssVars = calculateCardPositioning(index, cards.length);
        const linkUrl = isFilled.link(link) ? (link.url ?? '') : '';

        // Link if typeform add custom parameter to the link
        const hasTypeformLink = isTypeformLink(linkUrl);
        const typeformId = hasTypeformLink ? getTypeformIdFromLink(linkUrl) : null;
        return (
          <div
            key={id}
            className={cn(
              'translate-y-[var(--y)] scale-[var(--scale)] opacity-[var(--opacity)] transition-[transform,height] duration-200',
              !isOnDisplay && 'absolute top-0 h-[200px] overflow-hidden'
            )}
            style={cssVars}
          >
            <InfoCard shineBorder={isOnDisplay}>
              <InfoCardContent>
                <InfoCardDismiss onDismiss={() => onDismiss(id, getFirstRichTextText(title))}>
                  <Button variant='ghost' color='default' size='sm' icon={<Icon name='x' size='xs' />} className='absolute top-1.5 right-1.5' />
                </InfoCardDismiss>
                <InfoCardTitle>
                  <PrismicRichText field={title} className='line-clamp-2 font-semibold text-primary' />
                </InfoCardTitle>
                <InfoCardDescription>
                  <PrismicRichText field={description} className='line-clamp-3 text-default' />
                </InfoCardDescription>
                <InfoCardMedia
                  media={[
                    {
                      type: 'image',
                      src: isFilled.image(imagem) ? imagem.url : '',
                      alt: isFilled.image(imagem) ? imagem.alt || '' : '',
                      className: 'h-[200px] w-full',
                    },
                  ]}
                  loading='lazy'
                  shrinkHeight={75}
                  expandHeight={150}
                />
                <InfoCardFooter className='flex justify-end'>
                  {typeformId ? (
                    <>
                      <Button
                        size='sm'
                        variant='link'
                        color='primary'
                        onClick={() => {
                          Analytics.track('AppAnnouncement - Link Click', {
                            announcementId: id,
                            title: getFirstRichTextText(title),
                            linkText: link_text,
                          });
                          setOpenTypeformSurvey(typeformId);
                        }}
                      >
                        {link_text} <Icon name='arrow-up-right' className='ml-1' size='xs' />
                      </Button>
                      <DialogTypeformSurvey typeformId={typeformId} open={openTypeformSurvey === typeformId} onClose={() => setOpenTypeformSurvey(null)} />
                    </>
                  ) : (
                    <PrismicLink field={link} target='_blank'>
                      <Button
                        size='sm'
                        variant='link'
                        color='primary'
                        onClick={() => {
                          Analytics.track('AppAnnouncement - Link Click', {
                            announcementId: id,
                            title: getFirstRichTextText(title),
                            linkText: link_text,
                          });
                        }}
                      >
                        {link_text} <Icon name='arrow-up-right' className='ml-1' size='xs' />
                      </Button>
                    </PrismicLink>
                  )}
                </InfoCardFooter>
              </InfoCardContent>
            </InfoCard>
          </div>
        );
      })}
    </div>
  );
};

export default AppAnnouncementCard;
